import React, { useState } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { RefreshCcw, CheckCircle2, XCircle, Cpu, Tag, Clock, Radio, ChevronDown, ChevronRight } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

const APP_LABELS = {
    souvera_mail: "Souvera Mail",
    souvera_central: "Souvera Central",
    souvera_shield: "Souvera Shield",
    souvera_mailarchiv: "Souvera Mailarchiv",
};

const CHANNEL_CLS = {
    stable: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    dev: "bg-amber-500/10 text-amber-400 border-amber-500/30",
};

function fmtAgoMin(m) {
    if (m == null) return "never";
    if (m < 1) return "just now";
    if (m < 60) return `${m} min ago`;
    if (m < 1440) return `${Math.round(m / 60)} h ago`;
    return `${Math.round(m / 1440)} days ago`;
}

function fmtDate(iso) {
    if (!iso) return "—";
    return iso.slice(0, 16).replace("T", " ");
}

function AppDevopsCard({ appId, info }) {
    const [showReleases, setShowReleases] = useState(false);
    const installed = info?.installed || "?";
    const channel = info?.channel || "stable";
    const lastCheck = info?.last_check_ago_min;
    const releases = info?.releases || [];
    const latestTag = releases[0]?.tag || "?";
    const outdated = latestTag !== "?" && installed !== "?" && !latestTag.includes(installed);

    return (
        <div className="cm-card p-3 border border-zinc-800" data-testid={`souvera-devops-app-${appId}`}>
            <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono text-zinc-200">
                    {APP_LABELS[appId] || appId}
                </span>
                <span className={`px-1 py-0 text-[10px] font-mono uppercase rounded-sm border ${CHANNEL_CLS[channel] || CHANNEL_CLS.stable}`}>
                    <Radio size={9} className="inline mr-0.5" />
                    {channel}
                </span>
            </div>

            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono">
                <span className="text-zinc-500">Installed</span>
                <span className={`text-right ${outdated ? "text-amber-400" : "text-emerald-400"}`}>
                    v{installed}
                    {outdated && <span className="ml-1 text-zinc-600">(→ {latestTag})</span>}
                </span>

                <span className="text-zinc-500">Last check</span>
                <span className="text-right text-zinc-400">
                    <Clock size={9} className="inline mr-0.5" />
                    {fmtAgoMin(lastCheck)}
                </span>
            </div>

            {releases.length > 0 && (
                <div className="mt-2">
                    <button
                        onClick={() => setShowReleases(!showReleases)}
                        className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-300 font-mono"
                        data-testid={`souvera-devops-releases-toggle-${appId}`}
                    >
                        {showReleases ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                        Releases ({releases.length})
                    </button>
                    {showReleases && (
                        <div className="mt-1 space-y-0.5">
                            {releases.map((r, i) => (
                                <div
                                    key={r.tag}
                                    className={`flex items-center justify-between text-[10px] font-mono py-0.5 px-1 rounded-sm ${
                                        i === 0 ? "bg-sky-500/10 text-sky-300" : "text-zinc-500"
                                    }`}
                                >
                                    <span className="flex items-center gap-1">
                                        <Tag size={8} />
                                        {r.tag}
                                        {i === 0 && (
                                            <span className="text-[11px] text-zinc-600">latest</span>
                                        )}
                                    </span>
                                    <span>{fmtDate(r.published)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function SouveraDevopsPanel({ cloudId }) {
    const { data, mutate } = useSWR(
        `/api/admin/clouds/${cloudId}/souvera/devops-status`,
        fetcher,
        { refreshInterval: 0 },
    );
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const refresh = async () => {
        setBusy(true);
        setErr(null);
        try {
            await mutate();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };

    const s = data || {};
    const d = s.data || {};

    // Detect per-app devops structure: keys matching souvera_* are app statuses.
    const appKeys = Object.keys(d).filter((k) => k.startsWith("souvera_"));
    const hasAppData = appKeys.length > 0;

    return (
        <div className="cm-card p-5" data-testid="souvera-devops-panel">
            <div className="cm-h3 mb-3 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                    <Cpu size={14} className="text-sky-400" /> Souvera DevOps status
                </span>
                <button
                    type="button"
                    data-testid="souvera-devops-refresh-button"
                    disabled={busy}
                    onClick={refresh}
                    className="text-[11px] font-mono text-zinc-500 hover:text-zinc-200 disabled:opacity-40 flex items-center gap-1"
                    title="Query Souvera DevOps status now"
                >
                    <RefreshCcw size={10} /> Refresh
                </button>
            </div>

            {!s.reachable && s.reachable !== undefined ? (
                <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs">
                        <XCircle size={12} className="text-red-400" />
                        <span className="text-red-400">Not reachable</span>
                    </div>
                    {s.error && (
                        <p className="text-[11px] text-red-300/70 font-mono">{s.error}</p>
                    )}
                </div>
            ) : s.reachable ? (
                <div className="space-y-2">
                    <div className="flex items-center gap-1 text-[11px] text-emerald-400 font-mono">
                        <CheckCircle2 size={12} />
                        Connected ({s.fqdn})
                    </div>

                    {hasAppData ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {appKeys.map((appId) => (
                                <AppDevopsCard key={appId} appId={appId} info={d[appId]} />
                            ))}
                        </div>
                    ) : (
                        <p className="text-[11px] text-zinc-500 font-mono">
                            No app data available.
                        </p>
                    )}
                </div>
            ) : (
                <p className="text-xs text-zinc-500" data-testid="souvera-devops-never-polled">
                    Not queried yet. Click &quot;Refresh&quot; to load the status.
                </p>
            )}

            {err && (
                <p className="text-xs text-red-400 mt-2">{err}</p>
            )}
        </div>
    );
}
